import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, obj = user
  const [config, setConfig] = useState({ signup_enabled: true });
  const [viewAsDriver, setViewAsDriverState] = useState(() => localStorage.getItem("curtron_view_as_driver") === "1");

  const setViewAsDriver = useCallback((val) => {
    setViewAsDriverState(val);
    if (val) localStorage.setItem("curtron_view_as_driver", "1");
    else localStorage.removeItem("curtron_view_as_driver");
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("curtron_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("curtron_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
    api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => {});
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("curtron_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    if (data.token) {
      localStorage.setItem("curtron_token", data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = () => {
    localStorage.removeItem("curtron_token");
    setViewAsDriver(false);
    setUser(false);
  };

  const refreshUser = loadMe;

  const isAdmin = user && user.role === "admin";
  // Effective (UI) role — lets an admin preview the driver experience.
  const effectiveIsAdmin = isAdmin && !viewAsDriver;

  return (
    <AuthContext.Provider value={{ user, config, login, signup, logout, refreshUser, isAdmin, effectiveIsAdmin, viewAsDriver, setViewAsDriver, apiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
